import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import useReveal from '../hooks/useReveal';
import {
  buildCategoryTree,
  firstNameOf,
  loadCategories,
  loadEvents,
  loadHomepageAssets,
  loadHomepageCopy,
  selectFeaturedCollections,
  selectHomepageContent,
  tradeStatusFor,
  upcomingEvents,
} from '../services/storefront';
import { HOMEPAGE_COPY_DEFAULTS, resolveHomepageCopy } from '../components/storefront/homepageCopy';
import HeroSection from '../components/home/HeroSection';
import CollectionMarquee from '../components/home/CollectionMarquee';
import CollectionsMosaic from '../components/home/CollectionsMosaic';
import TradeProgram from '../components/home/TradeProgram';
import FindUs from '../components/home/FindUs';
import CatalogIndex from '../components/home/CatalogIndex';
import '../styles/tradingPost.css';
import './HomePage.css';

const EMPTY_CONTENT = { hero: null, featuredProducts: [], featuredCategories: [] };

const HomePage = () => {
  const { isAuthenticated, isApproved, isAdmin, userProfile } = useAuth();
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [copy, setCopy] = useState(HOMEPAGE_COPY_DEFAULTS);
  const [status, setStatus] = useState('loading');
  const rootRef = useRef(null);

  // Homepage assets and categories load independently: if one request fails
  // the page still renders everything the other one can support.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([loadHomepageAssets(), loadCategories()]).then(([assets, cats]) => {
      if (cancelled) return;
      if (assets.status === 'fulfilled') setContent(selectHomepageContent(assets.value));
      else console.error('Error fetching homepage assets:', assets.reason);
      if (cats.status === 'fulfilled') setCategories(cats.value);
      else console.error('Error fetching categories:', cats.reason);
      setStatus(assets.status === 'fulfilled' || cats.status === 'fulfilled' ? 'ready' : 'error');
    });
    loadEvents()
      .then((list) => {
        if (!cancelled) setEvents(upcomingEvents(list));
      })
      .catch(() => {});
    loadHomepageCopy().then((overrides) => {
      if (!cancelled) setCopy(resolveHomepageCopy(overrides));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useReveal(rootRef, `${status}-${events.length}`);

  const tree = useMemo(
    () => buildCategoryTree(categories, content.featuredCategories),
    [categories, content.featuredCategories],
  );

  const collections = useMemo(
    () => selectFeaturedCollections(content.featuredCategories, tree),
    [content.featuredCategories, tree],
  );

  const tradeStatus = tradeStatusFor({ isAuthenticated, isApproved, isAdmin });
  const loading = status === 'loading';

  return (
    <div className="tp-home" ref={rootRef}>
      <HeroSection
        hero={content.hero}
        status={status}
        featuredProducts={content.featuredProducts}
        stats={{ collections: tree.roots.length, categories: tree.byId.size }}
        tradeStatus={tradeStatus}
        firstName={firstNameOf(userProfile)}
        copy={copy}
      />
      {collections.length > 0 && <CollectionMarquee collections={collections} />}
      {(loading || collections.length > 0) && (
        <CollectionsMosaic
          collections={collections}
          loading={loading}
          totalCollections={tree.roots.length}
          totalCategories={tree.byId.size}
          copy={copy}
        />
      )}
      <TradeProgram tradeStatus={tradeStatus} copy={copy} />
      <FindUs events={events} copy={copy} />
      {tree.roots.length > 0 && <CatalogIndex roots={tree.roots} copy={copy} />}
    </div>
  );
};

export default HomePage;
